import { clsx } from "clsx";
import { Grid } from "@/components/Grid/Grid";
import { KitFooter } from "@/components/kit/KitFooter/KitFooter";
import { KitNavbar } from "@/components/kit/KitNavbar/KitNavbar";
import "@/components/guide/guide-system.css";
import "./blog.css";
import type { BlogArticleView } from "@/utils/blog/types";
import { BlogAuthorBlock } from "./BlogAuthorBlock";
import { BlogLeadMagnetCard, BlogRailPromo } from "./BlogCta";
import { BlogFaq } from "./BlogFaq";
import { BlogMarkdown } from "./BlogMarkdown";
import { BlogPostCard } from "./BlogPostCard";
import { BlogTableOfContents } from "./BlogTableOfContents";
import { renderTitleEmphasis } from "./lib/renderTitleEmphasis";

export interface BlogArticlePageProps {
  view: BlogArticleView;
}

export function BlogArticlePage({ view }: BlogArticlePageProps) {
  return (
    <div className="guide-page min-h-screen" data-component="BlogArticlePage">
      <KitNavbar ctaLabel="Консультация" ctaHref="/coaching" />

      <div className="wrap blog-article-wrap">
        <nav className="blog-breadcrumbs" aria-label="Хлебные крошки">
          {view.breadcrumbs.map((crumb, index) => (
            <span key={crumb.label} className="blog-crumb">
              {crumb.href ? (
                <a href={crumb.href}>{crumb.label}</a>
              ) : (
                <span aria-current="page">{crumb.label}</span>
              )}
              {index < view.breadcrumbs.length - 1 ? (
                <span className="blog-crumb-sep" aria-hidden="true">
                  /
                </span>
              ) : null}
            </span>
          ))}
        </nav>

        <div className="blog-article-layout">
          <article className="blog-article-main" data-section="blog-article">
            <div className="blog-card-meta blog-article-meta">
              {view.tagLabel ? <span className="pill-tag lav">{view.tagLabel}</span> : null}
              <span className="blog-card-dot">
                {view.dateLabel}
                {view.dateLabel && view.readLabel ? " · " : ""}
                {view.readLabel}
              </span>
            </div>

            <h1 className="blog-article-title">{renderTitleEmphasis(view.title)}</h1>
            {view.lead ? <p className="blog-article-lead">{view.lead}</p> : null}

            <BlogAuthorBlock author={view.author} variant="row" />

            <div className={clsx("blog-article-cover", !view.cover && "blog-card-cover-empty")}>
              {view.cover ? <img src={view.cover} alt={view.coverAlt} loading="eager" /> : null}
            </div>

            <BlogTableOfContents headings={view.toc} className="blog-toc-inline" />

            <BlogMarkdown body={view.body} />

            <BlogLeadMagnetCard href={view.leadMagnetHref} />

            <BlogFaq items={view.faq} />

            <BlogAuthorBlock author={view.author} variant="card" />
          </article>

          <aside className="blog-rail" data-section="blog-rail">
            <div className="blog-rail-sticky">
              <BlogTableOfContents headings={view.toc} className="blog-toc-rail" />
              <BlogRailPromo href={view.leadMagnetHref} />
            </div>
          </aside>
        </div>

        {view.related.length > 0 ? (
          <section className="blog-related" data-section="blog-related">
            <h2 className="blog-section-heading">Читайте также</h2>
            <Grid
              columns={{ base: 1, md: 2, lg: 3 }}
              gap={{ base: 32, lg: 40 }}
              className="blog-grid"
            >
              {view.related.map((post, index) => (
                <BlogPostCard key={post.slug} post={post} index={index} />
              ))}
            </Grid>
          </section>
        ) : null}
      </div>

      <KitFooter />
    </div>
  );
}

BlogArticlePage.displayName = "BlogArticlePage";
